import { type RouteComponentProps, useLocation } from '@gatsbyjs/reach-router'
import { navigate } from 'gatsby'
import {
  Body,
  BrandColors,
  Dialog,
  Divider,
  Icons,
  List,
  MediaQueries,
  rem,
  SizedBox,
  TextButton,
  UsersIcon,
} from '@collabhouse/shared'
import React, { type FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import CollaborationCard from '../../components/collaboration-card/CollaborationCard'
import Grid, { GridItem } from '../../components/grid/Grid'
import InternalLink from '../../components/internal-link/InternalLink'
import ResponseState from '../../components/response-state/ResponseState'
import { CollaborationState } from '../../graphql/generated'
import { useCollaborations } from '../../hooks/useCollaborations'
import { useGroup } from '../../hooks/useGroup'
import { type Collaboration } from '../../providers/CollaborationProvider.types'
import { collaborationStateDisplay } from '../../utils/collaboration'
import { getUrlWithParams, routes } from '../../utils/routes'

interface MyCollaborationsOverviewProps {
  location: {
    state: {
      paymentSuccess: boolean | undefined
      paymentTo: string | undefined
    }
  }
}

const getFilterOption = (state: CollaborationState) => {
  switch (state) {
    case CollaborationState.Accepted:
    case CollaborationState.Ongoing:
    case CollaborationState.DraftDelivered: {
      return {
        label: collaborationStateDisplay[CollaborationState.Accepted],
        path: routes.myCollabs.overview.running.url,
        value: [CollaborationState.Accepted, CollaborationState.Ongoing, CollaborationState.DraftDelivered],
      }
    }
    case CollaborationState.UpcomingPayment:
    case CollaborationState.PaymentRequired: {
      return {
        label: collaborationStateDisplay[CollaborationState.UpcomingPayment],
        path: routes.myCollabs.overview.upcomingPayment.url,
        value: CollaborationState.UpcomingPayment,
      }
    }
    case CollaborationState.OngoingPayment: {
      return {
        label: collaborationStateDisplay[CollaborationState.OngoingPayment],
        path: routes.myCollabs.overview.ongoingPayment.url,
        value: CollaborationState.OngoingPayment,
      }
    }
    case CollaborationState.Canceled: {
      return {
        label: collaborationStateDisplay[CollaborationState.Canceled],
        path: routes.myCollabs.overview.cancelled.url,
        value: CollaborationState.Canceled,
      }
    }
    case CollaborationState.Closed:
    case CollaborationState.Paid:
    case CollaborationState.DraftAccepted: {
      return {
        label: collaborationStateDisplay[CollaborationState.Closed],
        path: routes.myCollabs.overview.done.url,
        value: [CollaborationState.Closed, CollaborationState.Paid, CollaborationState.DraftAccepted],
      }
    }
    case CollaborationState.Declined: {
      return {
        label: collaborationStateDisplay[CollaborationState.Declined],
        path: routes.myCollabs.overview.declined.url,
        value: CollaborationState.Declined,
      }
    }

    case CollaborationState.Proposed: {
      return {
        label: collaborationStateDisplay[CollaborationState.Proposed],
        path: routes.myCollabs.overview.proposal.url,
        value: CollaborationState.Proposed,
      }
    }
    default: {
      return null
    }
  }
}

const MyCollaborationsOverview: FunctionComponent<
  React.PropsWithChildren<RouteComponentProps<MyCollaborationsOverviewProps>>
> = ({ location }) => {
  const { activeFilters, loading, error, collaborations, collaborationStates, setActiveFilters } = useCollaborations()
  const { group, redirectIfNoGroup } = useGroup()
  const { pathname } = useLocation()
  const [showPaymentSuccessDialog, setShowPaymentSuccessDialog] = useState(location?.state?.paymentSuccess || false)

  const filters = useMemo(() => {
    const filters =
      collaborationStates
        .map((state: CollaborationState) => getFilterOption(state))
        .filter(
          (value, index, self) =>
            index ===
            self.findIndex(
              (filterOption) => filterOption?.label === value?.label && filterOption?.path === value?.path,
            ),
        )
        .filter(Boolean) ?? []

    filters.unshift({
      label: 'All',
      path: routes.myCollabs.overview.url,
      value: null,
    })
    return filters
  }, [collaborationStates])

  useEffect(() => {
    const newFilter = filters.find((filter) => filter.path === pathname)?.value ?? ''

    if (newFilter !== activeFilters.status) {
      setActiveFilters({
        nextAction: '',
        status: newFilter,
      })
    }
  }, [setActiveFilters, activeFilters, pathname, filters])

  const onCollaborationClick = useCallback((collaboration: Collaboration) => {
    const myGroupIsTheArtist = collaboration.from.id === group?.id

    if (collaboration.state === CollaborationState.PaymentRequired && myGroupIsTheArtist) {
      void navigate(
        getUrlWithParams(routes.collaboration.details.payment.url, {
          collaborationId: collaboration.id,
        }),
      )
    } else {
      void navigate(
        getUrlWithParams(routes.collaboration.details.url, {
          collaborationId: collaboration.id,
        }),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (redirectIfNoGroup()) return null

  return (
    <>
      <Grid>
        <GridItem desktop={12} mobile={12}>
          {!group?.id ? (
            emptyState
          ) : (
            <>
              {loading && <ResponseState variant="loading" title="One moment please, we're loading your collabs." />}

              {!loading && !error && (
                <>
                  <FilterWrapper>
                    <Filters>
                      <>
                        {filters?.map(
                          (filterOption, key) =>
                            filterOption && (
                              <StyledTextButton
                                key={key}
                                variant="secondary"
                                size="medium"
                                onClick={() => {
                                  void navigate(filterOption.path.toString())
                                }}
                                active={pathname === filterOption.path}
                              >
                                {filterOption.label}
                              </StyledTextButton>
                            ),
                        )}
                      </>
                    </Filters>
                  </FilterWrapper>
                  <Divider />
                  <SizedBox height={{ desktop: rem(48), mobile: rem(24) }} />
                  {collaborations?.length > 0 ? (
                    <List separator={<SizedBox height={rem(16)} />}>
                      {collaborations.map((collaboration) => (
                        <CollaborationCard
                          key={collaboration.id}
                          collaboration={collaboration}
                          onClick={() => onCollaborationClick(collaboration)}
                          detailsButtonLabel={
                            collaboration.state === CollaborationState.Proposed
                              ? 'View proposals details'
                              : 'View collab'
                          }
                        />
                      ))}
                    </List>
                  ) : (
                    emptyState
                  )}
                </>
              )}

              {!loading && error && (
                <ResponseState
                  icon={Icons.warning}
                  variant="error"
                  title="Oops, something went wrong."
                  body="This page didn't load correctly. Please try refreshing the page or come back later."
                />
              )}
            </>
          )}
        </GridItem>
      </Grid>

      <Dialog
        visible={showPaymentSuccessDialog}
        title="The payment was successful!"
        onClose={() => setShowPaymentSuccessDialog(false)}
        actions={
          <TextButton variant="primary" onClick={() => setShowPaymentSuccessDialog(false)}>
            Close
          </TextButton>
        }
      >
        <>
          <SizedBox height={16} />
          <Body>
            {`Your collab with ${location?.state?.paymentTo} is a wrap. We hope
              it caused the boost you were looking for.`}
          </Body>
        </>
      </Dialog>
    </>
  )
}

export default MyCollaborationsOverview

const StyledUsersIcon = styled(UsersIcon)`
  width: 72px;
  height: 72px;

  ${MediaQueries.desktop} {
    width: 96px;
    height: 96px;
  }
`
const emptyState = (
  <ResponseState
    customIcon={<StyledUsersIcon />}
    variant="empty"
    title="No collabs found."
    body={
      <>
        Go to the <InternalLink to={routes.collaboration.overview.url}>Collab Market</InternalLink> to start a collab.
      </>
    }
  />
)

const StyledTextButton = styled(TextButton)<{ active: boolean }>`
  margin-right: 2rem;
  background-size: 50px 2px;
  display: inline-block;
  padding-bottom: ${rem(14)};

  ${({ active }) =>
    active &&
    `background-image: linear-gradient(
        ${BrandColors.springGreen.toString()},
        ${BrandColors.springGreen.toString()}
      ); `}
  ${MediaQueries.desktop} {
    display: inline-flex;
  }
`

const Filters = styled.div`
  white-space: nowrap;

  ${MediaQueries.desktop} {
    display: flex;
  }
`

const FilterWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`
